import { chipsByTask, selectedOptions, toggleAction } from './fieldChips';
import { useProjectFieldValues, useProjectFields, useSetTaskField } from './queries';
import type { FieldChip } from './fieldChips';

/**
 * Setting a task's custom fields, on the task itself.
 *
 * ── Why buttons and not a dropdown ─────────────────────────────────────────
 *
 * Every option is visible, so choosing one is a click rather than a click,
 * a read and a click. These sets are small by design — the largest is eight
 * camera codes — and a dropdown would hide the vocabulary at exactly the
 * moment somebody is trying to learn it. The Asana board's fields sat empty on
 * 368 tasks; making the options invisible is not the way to fix that.
 *
 * Clicking a selected option UNSETS it. "No answer" is a real state, and a
 * separate clear control is one nobody finds.
 */
export function FieldPicker({
  projectId,
  taskId,
  canEdit,
}: {
  projectId: string;
  taskId: string;
  canEdit: boolean;
}) {
  const definitions = useProjectFields(projectId);
  const values = useProjectFieldValues(projectId);
  const setField = useSetTaskField(projectId);

  const fields = definitions.data ?? [];
  if (fields.length === 0) {
    // A project with no fields shows nothing at all — not an empty heading.
    // Most projects have none and never will.
    return null;
  }

  const chips = chipsByTask(fields, values.data ?? []).get(taskId) ?? [];

  return (
    <div className="field-picker">
      {fields.map((field) => {
        const selected = selectedOptions(chips, field.field_id);
        return (
          <div key={field.field_id} className="field-picker-row">
            <span className="field-picker-label">
              {field.name}
              {field.is_multi ? <span className="field-picker-multi">choose any</span> : null}
            </span>
            <div className="field-picker-options">
              {field.options.map((option) => {
                const on = selected.has(option.id);
                return (
                  <button
                    key={option.id}
                    type="button"
                    disabled={!canEdit || setField.isPending}
                    aria-pressed={on}
                    className={
                      on
                        ? `field-chip field-chip--${option.color_key} field-chip--on`
                        : `field-chip field-chip--${option.color_key}`
                    }
                    onClick={() =>
                      setField.mutate({
                        taskId,
                        fieldId: field.field_id,
                        action: toggleAction(field, selected, option.id),
                      })
                    }
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** The same values, read-only, as they appear on a board card. */
export function FieldChips({ chips }: { chips: FieldChip[] }) {
  if (chips.length === 0) {
    return null;
  }
  return (
    <span className="board-card-fields">
      {chips.map((chip) => (
        <span
          key={`${chip.fieldId}-${chip.optionId}`}
          className={`field-chip field-chip--${chip.colorKey} field-chip--on`}
        >
          {chip.label}
        </span>
      ))}
    </span>
  );
}
